import type { ElementType, ThermalLayer } from '../calculation/types';

export interface CalculatorLayer extends ThermalLayer {
  category: string;
  accent: 'neutral' | 'air' | 'sisalwool' | 'lining' | 'structure';
  editableThickness?: boolean;
  editableConductivity?: boolean;
  removable?: boolean;
  helper?: string;
}

export interface ConstructionTemplate {
  id: string;
  element: ElementType;
  name: string;
  description: string;
  externalSurfaceResistanceM2KW: number;
  internalSurfaceResistanceM2KW: number;
  bridge?: {
    label: string;
    memberName: string;
    widthMm: number;
    spacingMm: number;
    conductivityWmK: number;
  };
  layers: CalculatorLayer[];
}

export const elementOptions: Array<{
  id: ElementType;
  name: string;
  description: string;
}> = [
  {
    id: 'roof',
    name: 'Roof',
    description: 'Pitched roofs, lofts and ceiling-level insulation.',
  },
  {
    id: 'wall',
    name: 'External wall',
    description: 'Timber-frame and masonry wall build-ups.',
  },
  {
    id: 'floor',
    name: 'Floor',
    description: 'Suspended timber floors insulated between joists.',
  },
];

const sisalLayer = (
  id: string,
  name: string,
  thicknessMm: number,
  bridgeFraction?: number,
): CalculatorLayer => ({
  id,
  name,
  category: 'Sisalwool insulation',
  accent: 'sisalwool',
  thicknessMm,
  conductivityWmK: 0.039,
  editableThickness: true,
  editableConductivity: true,
  helper: 'Example conductivity value — confirm against the selected product declaration.',
  bridge:
    bridgeFraction === undefined
      ? undefined
      : { fraction: bridgeFraction, conductivityWmK: 0.13 },
});

export const constructionTemplates: ConstructionTemplate[] = [
  {
    id: 'pitched-rafter',
    element: 'roof',
    name: 'Pitched roof — rafter level',
    description: 'Insulation fitted between timber rafters with a service void below.',
    externalSurfaceResistanceM2KW: 0.04,
    internalSurfaceResistanceM2KW: 0.1,
    bridge: {
      label: 'Repeating timber bridge',
      memberName: 'Softwood rafters',
      widthMm: 47,
      spacingMm: 600,
      conductivityWmK: 0.13,
    },
    layers: [
      {
        id: 'roof-cover',
        name: 'Roof covering (slate or tiles)',
        category: 'External finish',
        accent: 'neutral',
        thicknessMm: 20,
        resistanceOverrideM2KW: 0.02,
        editableThickness: true,
        removable: true,
      },
      {
        id: 'ventilated-air',
        name: 'Ventilated air layer',
        category: 'Air layer',
        accent: 'air',
        thicknessMm: 50,
        ventilated: true,
        editableThickness: true,
        removable: true,
        helper: 'A well-ventilated layer is excluded from thermal resistance.',
      },
      sisalLayer('sisal-rafter', 'Sisalwool between rafters', 150, 47 / 600),
      {
        id: 'service-void',
        name: 'Service void / unventilated air layer',
        category: 'Air layer',
        accent: 'air',
        thicknessMm: 25,
        resistanceOverrideM2KW: 0.18,
        editableThickness: true,
        removable: true,
      },
      {
        id: 'plasterboard',
        name: 'Internal lining (plasterboard)',
        category: 'Internal lining',
        accent: 'lining',
        thicknessMm: 12.5,
        conductivityWmK: 0.21,
        editableThickness: true,
        removable: true,
      },
    ],
  },
  {
    id: 'loft-ceiling',
    element: 'roof',
    name: 'Pitched roof — ceiling level',
    description: 'Loft insulation over and between ceiling joists below a cold roof void.',
    externalSurfaceResistanceM2KW: 0.04,
    internalSurfaceResistanceM2KW: 0.1,
    bridge: {
      label: 'Repeating timber bridge',
      memberName: 'Ceiling joists',
      widthMm: 47,
      spacingMm: 600,
      conductivityWmK: 0.13,
    },
    layers: [
      {
        id: 'loft-void',
        name: 'Ventilated loft void',
        category: 'Air layer',
        accent: 'air',
        ventilated: true,
        resistanceOverrideM2KW: 0,
        helper: 'The ventilated roof space is outside the thermal envelope.',
      },
      sisalLayer('sisal-loft', 'Sisalwool loft insulation', 200, 47 / 600),
      {
        id: 'ceiling-board',
        name: 'Plasterboard ceiling',
        category: 'Internal lining',
        accent: 'lining',
        thicknessMm: 12.5,
        conductivityWmK: 0.21,
        editableThickness: true,
        removable: true,
      },
    ],
  },
  {
    id: 'timber-frame-wall',
    element: 'wall',
    name: 'Timber-frame wall',
    description: 'Sisalwool fitted between structural timber studs.',
    externalSurfaceResistanceM2KW: 0.04,
    internalSurfaceResistanceM2KW: 0.13,
    bridge: {
      label: 'Repeating timber bridge',
      memberName: 'Softwood studs',
      widthMm: 38,
      spacingMm: 600,
      conductivityWmK: 0.13,
    },
    layers: [
      {
        id: 'outer-sheathing',
        name: 'External sheathing board',
        category: 'Sheathing',
        accent: 'structure',
        thicknessMm: 12,
        conductivityWmK: 0.13,
        editableThickness: true,
        removable: true,
      },
      sisalLayer('sisal-stud', 'Sisalwool between timber studs', 140, 38 / 600),
      {
        id: 'wall-service-void',
        name: 'Service void / unventilated air layer',
        category: 'Air layer',
        accent: 'air',
        thicknessMm: 25,
        resistanceOverrideM2KW: 0.18,
        editableThickness: true,
        removable: true,
      },
      {
        id: 'wall-plasterboard',
        name: 'Internal lining (plasterboard)',
        category: 'Internal lining',
        accent: 'lining',
        thicknessMm: 12.5,
        conductivityWmK: 0.21,
        editableThickness: true,
        removable: true,
      },
    ],
  },
  {
    id: 'masonry-wall',
    element: 'wall',
    name: 'Masonry cavity wall',
    description: 'A generic masonry wall with insulation in the cavity.',
    externalSurfaceResistanceM2KW: 0.04,
    internalSurfaceResistanceM2KW: 0.13,
    layers: [
      {
        id: 'brick',
        name: 'Facing brick outer leaf',
        category: 'Masonry',
        accent: 'structure',
        thicknessMm: 102.5,
        conductivityWmK: 0.77,
        editableThickness: true,
        removable: true,
      },
      sisalLayer('sisal-cavity', 'Sisalwool cavity insulation', 100),
      {
        id: 'block',
        name: 'Medium-density block inner leaf',
        category: 'Masonry',
        accent: 'structure',
        thicknessMm: 100,
        conductivityWmK: 0.51,
        editableThickness: true,
        editableConductivity: true,
        removable: true,
      },
      {
        id: 'wall-finish',
        name: 'Internal plasterboard lining',
        category: 'Internal lining',
        accent: 'lining',
        thicknessMm: 12.5,
        conductivityWmK: 0.21,
        editableThickness: true,
        removable: true,
      },
    ],
  },
  {
    id: 'suspended-timber-floor',
    element: 'floor',
    name: 'Suspended timber floor',
    description: 'Insulation supported between timber joists above a ventilated void.',
    externalSurfaceResistanceM2KW: 0.04,
    internalSurfaceResistanceM2KW: 0.17,
    bridge: {
      label: 'Repeating timber bridge',
      memberName: 'Softwood floor joists',
      widthMm: 47,
      spacingMm: 400,
      conductivityWmK: 0.13,
    },
    layers: [
      {
        id: 'floor-deck',
        name: 'Timber floor deck',
        category: 'Floor finish',
        accent: 'structure',
        thicknessMm: 22,
        conductivityWmK: 0.13,
        editableThickness: true,
        removable: true,
      },
      sisalLayer('sisal-joist', 'Sisalwool between floor joists', 140, 47 / 400),
      {
        id: 'floor-void',
        name: 'Ventilated sub-floor void',
        category: 'Air layer',
        accent: 'air',
        ventilated: true,
        resistanceOverrideM2KW: 0,
        helper: 'The ventilated void is outside the thermal envelope.',
      },
    ],
  },
];

export function cloneTemplate(template: ConstructionTemplate): ConstructionTemplate {
  return structuredClone(template);
}
